import { Box, Typography, useTheme } from '@mui/material';
import { CinemaModel, useGetCinemaDetails } from '@/logic/services/cinemas';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { CheckGlobalSsrErrors } from '@/helpers/check-global-ssr-errors';

import { ExperienceModel } from '@/logic/services/experiences';
import ExperiencesSlider from '@/components/cinema/experiences-slider';
import GoogleMaps from '@/components/shared/map';
import IconLocation from '@/components/shared/icon-location';
import { MovieTypesEnum } from '@/constants/movie-types-enum';
import MoviesList from '@/components/shared/movies-list';
import Seo from '@/components/seo/seo';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useGetInfiniteMovies } from '@/logic/services';
import { useRouter } from 'next/router';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const EmptyCinemaImgCard = '/icons/cinema/emptyCinemaImgCard.svg';

const paginationShowing = {
  limit: 4,
  page: 1,
};

export const CinemaDetails: NextPage<{
  path: string;
}> = ({ path }) => {
  const router = useRouter();
  const { cityId: browserCityId } = useCity();
  const cityId = +router.query.cityId || browserCityId;
  const theme = useTheme();
  const intl = useIntl();

  const { data: pageData, isLoading } = useGetCinemaDetails({
    query: { slug: router.query.slug as string },
    params: {
      cityId: cityId,
    },
  });

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useGetInfiniteMovies({
      params: {
        cinemaIds: [pageData?.cinema?.id],
        cityId: cityId,
        limit: paginationShowing.limit,
        page: paginationShowing.page,
        type: MovieTypesEnum.SHOW_NOW,
      },
    });

  const movies = useMemo(
    () => (data?.pages ? data?.pages?.flatMap(group => group.data) : []),
    [data]
  );

  return (
    <>
      <Seo
        title={
          pageData?.cinema?.titleTag ||
          intl.formatMessage({
            id: 'cinema_details',
          })
        }
        canonical={pageData?.cinema?.canonicalURL}
        pagePath={path}
        description={pageData?.cinema?.descriptionTag || pageData?.cinema?.description}
        image={pageData?.cinema?.image}
        noFollow={pageData?.cinema?.hasFollow}
        noIndex={pageData?.cinema?.hasIndex}
      />
      <Box
        component='section'
        sx={{
          flexGrow: 1,
          mb: pxToRem(122),
          '& h1,h2,h3,h4,h5': {
            fontWeight: 700,
          },
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: pxToRem(30),
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            px: {
              laptop: pxToRem(128),
              tablet: pxToRem(60),
              mobile: '18px',
            },
            maxWidth: '1920px',
            mx: 'auto',
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='cinema_details' />
        </Typography>
        {!isLoading ? (
          <Box component='div'>
            <ImageBoxFuture
              img={pageData?.cinema?.coverUrl || EmptyCinemaImgCard}
              title={pageData?.cinema?.coverUrlAlt || pageData?.cinema?.name}
              layout='intrinsic'
              objectFit='cover'
              width='1920px'
              height='1080px'
              sx={{
                marginBottom: pxToRem(32),
              }}
            />
            <Box
              component='div'
              sx={{
                px: {
                  laptop: pxToRem(128),
                  tablet: pxToRem(60),
                  mobile: '16px',
                },
                maxWidth: '1920px',
                mx: 'auto',
                mb: pxToRem(48),
              }}
            >
              <Box component='article'>
                <Typography
                  gutterBottom
                  component='h2'
                  sx={{
                    fontSize: { tablet: pxToRem(28), mobile: '18px' },
                    mb: pxToRem(16),
                    color: theme.palette.grey[90],
                  }}
                >
                  {pageData?.cinema?.name}
                </Typography>
                <Box component='div' sx={{ display: 'flex', mb: pxToRem(48) }}>
                  <IconLocation />
                  <Typography
                    gutterBottom
                    component='address'
                    sx={{
                      mx: pxToRem(12),
                      fontSize: { tablet: pxToRem(24), mobile: '16px' },
                      color: theme.palette.grey[50],
                    }}
                  >
                    {pageData?.cinema?.address1}
                  </Typography>
                </Box>
              </Box>
              <Box component='article'>
                <Typography
                  component='h4'
                  sx={{
                    fontSize: { tablet: pxToRem(24), mobile: '16px' },
                    color: theme.palette.grey[90],
                    mb: pxToRem(32),
                  }}
                >
                  <FormattedMessage id='cinema_details.about' />
                </Typography>
                <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{
                    fontSize: { tablet: pxToRem(24), mobile: '16px' },

                    color: theme.palette.grey[50],
                    maxWidth: '1096px',
                    lineHeight: '150%',
                  }}
                >
                  {pageData?.cinema?.description}
                </Typography>
              </Box>
            </Box>

            <Box component='div' sx={{ overflow: 'hidden' }}>
              {pageData?.cinema?.experiences.length > 0 && (
                <>
                  <Typography
                    component='h4'
                    sx={{
                      fontSize: { tablet: pxToRem(24), mobile: '16px' },

                      color: theme.palette.grey[90],
                      px: {
                        laptop: pxToRem(128),
                        tablet: pxToRem(60),
                        mobile: '16px',
                      },
                    }}
                  >
                    <FormattedMessage id='cinema_details.available_experiences' />
                  </Typography>

                  <ExperiencesSlider
                    experiences={pageData?.cinema?.experiences as ExperienceModel[]}
                  />
                </>
              )}
              <Box
                component='div'
                sx={{
                  px: {
                    laptop: pxToRem(128),
                    tablet: pxToRem(60),
                    mobile: '16px',
                  },
                  py: {
                    laptop: pxToRem(128),
                    tablet: pxToRem(60),
                    mobile: '16px',
                  },
                }}
              >
                <Box
                  component='div'
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                  }}
                >
                  <Box
                    component='div'
                    sx={{
                      display: 'flex',
                      mb: { tablet: pxToRem(33), mobile: '16px' },
                    }}
                  >
                    <IconLocation />

                    <Typography
                      gutterBottom
                      component='h2'
                      sx={{
                        fontSize: { tablet: pxToRem(24), mobile: '16px' },

                        mx: pxToRem(12),
                        color: theme.palette.grey[50],
                        mb: 0,
                      }}
                    >
                      {pageData?.cinema?.address2}
                    </Typography>
                  </Box>

                  {pageData?.cinema?.long && pageData?.cinema?.lat && (
                    <Typography
                      gutterBottom
                      component='a'
                      sx={{
                        fontSize: pxToRem(24),
                        textDecoration: 'none',
                        color: theme.palette.primary.main,
                        cursor: 'pointer',

                        mb: { tablet: pxToRem(33), mobile: '16px' },
                      }}
                      onClick={() => {
                        window.open(
                          'https://maps.google.com?q=' +
                            pageData?.cinema?.lat +
                            ',' +
                            pageData?.cinema?.long
                        );
                      }}
                    >
                      <FormattedMessage id='cinema_details.view_directions' />
                    </Typography>
                  )}
                </Box>

                {pageData?.cinema?.long && pageData?.cinema?.lat && (
                  <Box
                    component='div'
                    sx={{
                      height: { tablet: pxToRem(941), mobile: '152px' },

                      width: '100%',
                      borderRadius: '20px',
                      overflow: 'hidden',
                    }}
                  >
                    <GoogleMaps
                      long={+pageData?.cinema?.long}
                      lat={+pageData?.cinema?.lat}
                    />
                  </Box>
                )}
              </Box>
            </Box>
            {movies.length > 0 && (
              <Box
                component='div'
                sx={{
                  px: {
                    laptop: pxToRem(128),
                    tablet: pxToRem(60),
                    mobile: '16px',
                  },
                  maxWidth: '1920px',
                  mx: 'auto',
                  mb: pxToRem(500),
                }}
              >
                <Typography
                  component='h4'
                  sx={{
                    fontSize: pxToRem(24),
                    color: theme.palette.grey[90],
                    mt: { tablet: pxToRem(48), mobile: '24px' },
                    mb: { tablet: pxToRem(52), mobile: '16px' },
                  }}
                >
                  <FormattedMessage id='cinema_details.movies_showing' />
                </Typography>

                <MoviesList
                  moviesList={movies}
                  hasMore={hasNextPage}
                  fetchMovies={fetchNextPage}
                  isFetching={isFetching}
                  isFetchingNextPage={isFetchingNextPage}
                  sx={{ mt: { mobile: '0px' } }}
                  itemListName='Cinemas'
                />
              </Box>
            )}
          </Box>
        ) : (
          <MovieLoaderWrapper
            innerBox={{
              width: '160px',
              height: '160px',
              [theme.breakpoints.down('laptop')]: {
                width: '100px',
                height: '100px',
              },
            }}
            outerBox={{
              width: '100%',
              height: {
                mobile: 'calc(100vh - 56px)',
                tablet: 'calc(100vh - 100px)',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.query.cityId || context.req.cookies.city);

  const queryClient = new QueryClient();
  let cinema: CinemaModel;
  const cinemaSlug = context.query.slug as string;
  try {
    if (!!cinemaSlug && !!cityId) {
      cinema = await useGetCinemaDetails.fetchQuery(queryClient, {
        query: { slug: cinemaSlug },
        params: {
          cityId: cityId,
        },
        headers,
      });
    }

    if (!!cinema?.id) {
      await useGetInfiniteMovies.fetchQuery(queryClient, {
        params: {
          cinemaIds: [cinema?.id],
          cityId: cityId,
          type: MovieTypesEnum.SHOW_NOW,
          limit: paginationShowing.limit,
          page: paginationShowing.page,
        },
        headers,
      });
    }
  } catch (error) {
    return CheckGlobalSsrErrors(error);
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};
export default CinemaDetails;
